import * as React from 'react';
import { getDatasheetResultFolderFormats, getTdsResultFolderFormats } from '@modules/datasheets/services/formats';
import { useMessage } from '@common/hooks/message';
import { trDts } from '@modules/datasheets/utils/i18n';
import { createDatasheetFormat, getNotLoadedDatasheetComponents, saveFormatDefaults, updateDatasheetFormat } from '@modules/datasheets/services/datasheet';
import { DatasheetLocalGetDto } from '../../types/datasheet';
import { DatasheetElement } from '@modules/datasheets/types/datasheet-element';
import { LocalDatasheetTabDto } from '../../types/tab-dto';
import { convertDatasheetTabsToApiPayload, convertUIFormatsToApiDefaults } from '@modules/datasheets/utils/datasheet-format-converter';
import type { DatasheetElementSettingsFieldComponentValue } from '../../components/datasheet-element-settings/controllers/main';
import type { DatasheetElementSettingsTextComponentValue } from '../../components/datasheet-element-settings/controllers/main';

export const useDatasheetSavingController = (props: { 
  datasheet: DatasheetLocalGetDto;
  formatId: number;
  getTabElements: (tabId: string | number) => Record<string, DatasheetElement> | undefined;
  defaultFieldComponentSettings: DatasheetElementSettingsFieldComponentValue;
  defaultTextComponentSettings: DatasheetElementSettingsTextComponentValue;
  onSaveComplete?: (formatId: number, formatName: string, tableFieldId?: number | null) => void;
  tableFieldId?: number | null;
}) => {
  const { formatId } = props;
  const [folderPickerOpened, setFolderPickerOpened] = React.useState(false);
  const { showConfirmMessage, showInfoMessage, messageProps, closeMessage } = useMessage();
  const [saveSuccessMessage, setSaveSuccessMessage] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const clearSaveSuccessMessage = React.useCallback(() => {
    setSaveSuccessMessage(null);
  }, []);

  React.useEffect(() => {
    if (!saveSuccessMessage) {
      return;
    }
    const timer = window.setTimeout(() => setSaveSuccessMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [saveSuccessMessage]);

  const openFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(true);
  }, []);

  const closeFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(false);
  }, []);

  const mergeNotLoadedIntoTabs = (localTabs: any[], notLoaded: any) => {
    if (!notLoaded?.tabsData) return localTabs;

    const mergeField = (local: any, api: any) => {
      const headingPds =
        local.headingPds && String(local.headingPds).trim()
          ? local.headingPds
          : (api.headingPds || '');
      const headStyle =
        local.headStyle != null && local.headStyle !== 0
          ? local.headStyle
          : (api.headStyle ?? 33);
      const textStyle =
        local.textStyle != null && local.textStyle !== 0
          ? local.textStyle
          : (api.textStyle ?? 33);
      const fieldname =
        local.field?.fieldname && String(local.field.fieldname).trim()
          ? local.field.fieldname
          : (api.field?.fieldname || '');

      return {
        ...api,
        ...local,
        headingPds,
        headStyle,
        textStyle,
        field: {
          ...api.field,
          ...local.field,
          fieldname,
        },
      };
    };

    return localTabs.map((tab, tabIndex) => {
      const apiTab =
        notLoaded.tabsData.find((t: any) => String(t.id) === String(tab.id)) ??
        notLoaded.tabsData[tabIndex]; // fallback: match by index when ids differ

      if (!apiTab) return tab;
      const localFields = tab.fields || [];
      const apiFields = apiTab.fields || [];
      const seen = new Set<string>();

      const mergedFields: any[] = [];

      for (const apiField of apiFields) {
        const id = String(apiField.id ?? apiField.field?.fieldid);
        seen.add(id);
        const localField = localFields.find(
          (f: any) => String(f.id ?? f.field?.fieldid) === id
        );
        mergedFields.push(
          localField ? mergeField(localField, apiField) : apiField
        );
      }

      for (const localField of localFields) {
        const id = String(localField.id ?? localField.field?.fieldid);
        if (!seen.has(id)) {
          seen.add(id);
          mergedFields.push(localField);
        }
      }

      const localTexts = tab.texts || [];
      const apiTexts = apiTab.texts || [];

      const mergedTexts = [
        ...localTexts,
        ...apiTexts.filter((apiText: any) => {
          const apiId = apiText.id ?? apiText.idKey;
          return !localTexts.some((localText: any) => {
            const localId = localText.id ?? localText.idKey;
            return String(localId) === String(apiId);
          });
        }),
      ];

      return {
        ...tab,
        name: tab.name || apiTab.name,
        fields: mergedFields,
        texts: mergedTexts,
      };
    });
  };

  const handleSaveAsSubmit = React.useCallback(
    async (payload:any,context:number,isTDSFromEditor:boolean = false) => {
      // Determine if it's TDS based on tableFieldId (following getContextAndTds logic)
      const isTDS = isTDSFromEditor || (props.tableFieldId != null && props.tableFieldId !== 0);
      // For TDS: use context 1, tds: true (following getContextAndTds logic)
      // For regular DS: use context (1 or 2), tds: false
      const folderContext = isTDS ? 1 : context;
      const tds = isTDS;
      
      // Load formats - use TDS API if TDS, otherwise regular DS API
      let existingFormats;
      if (isTDS) {
        existingFormats = await getTdsResultFolderFormats({ 
          folderId: payload.format.folderId, 
          context: folderContext,
          tableId: props.tableFieldId! 
        });
      } else {
        existingFormats = await getDatasheetResultFolderFormats({ folderId: payload.format.folderId, context: context });
      }
      
      const createdFormat = existingFormats.find(
        (f: { name: string }) => f.name === payload.format.name.trim()
      );
      let tabs: Array<number> = [];
      props.datasheet.tabs.forEach((tab: LocalDatasheetTabDto, index: number) => {
        const tabId = typeof tab.id === 'string' ? index + 1 : tab.id;
        tabs.push(tabId);
      });

      const processSaveAs = async (newFormatId: number, oldFormatId: number) => {
        const notLoaded = await getNotLoadedDatasheetComponents({
          formatId: props.datasheet.id,
          tabs: tabs,
          ...(isTDS ? { isTDS: true } : {}),
        });

        const mergedTabs = mergeNotLoadedIntoTabs(props.datasheet.tabs, notLoaded);

        const formatPayload = convertDatasheetTabsToApiPayload(
          mergedTabs,
          newFormatId,
          oldFormatId,
          props.getTabElements,
          notLoaded,
          true
        );

        for (const tab of formatPayload.tabsData) {
          if (tab.texts) {
            for (const text of tab.texts) {
              text.idKey = -1;
            }
          }
        }

        await updateDatasheetFormat({
          id: formatPayload.id,
          oldFormatId: formatPayload.oldFormatId,
          tabsData: formatPayload.tabsData,
          tds: isTDS,
        });
        
        // For TDS: use context 8 for loadDSFormat API, but use context 1 for saveFormatDefaults
        // For regular DS: use the original context
        const saveDefaultsContext = isTDS ? 1 : context;
        await saveFormatDefaults({
          formatId: newFormatId,
          context: saveDefaultsContext,
          defaults: convertUIFormatsToApiDefaults(
            props.defaultFieldComponentSettings,
            props.defaultTextComponentSettings,
            props.datasheet.gridSettings,
            newFormatId,
            saveDefaultsContext
          ),
          tds: isTDS,
        });
        
        closeFolderPicker();
        if (props.onSaveComplete) {
          props.onSaveComplete(
            newFormatId, 
            createdFormat?.name || payload.format.name,
            isTDS ? props.tableFieldId! : null
          );
        }
      };

      if (createdFormat) {
        showConfirmMessage({
          cancelText: trDts("Cancel"),
          confirmText: trDts("Overwrite"),
          content: trDts("wouldYouLikeToOverwriteFormatName"),
          onCustomOk: async () => {
            await processSaveAs(createdFormat.id, props.datasheet.id);
          },
        });
      } else {
        // Create format - use type "tds" if TDS, otherwise "ds"
        await createDatasheetFormat({
          formatName: payload.format.name,
          folderId: payload.format.folderId,
          type: isTDS ? 'tds' : 'ds',
          tableId: isTDS ? props.tableFieldId! : undefined,
        });
        
        // Reload formats to find the created format
        let createdFormats;
        if (isTDS) {
          createdFormats = await getTdsResultFolderFormats({ 
            folderId: payload.format.folderId, 
            context: folderContext,
            tableId: props.tableFieldId! 
          });
        } else {
          createdFormats = await getDatasheetResultFolderFormats({ folderId: payload.format.folderId, context: context });
        }
        
        const createdFormat = createdFormats.find(
          (f: { name: string }) => f.name === payload.format.name.trim()
        );
        
        if (!createdFormat) {
          console.error("Failed to find created format");
          return;
        }
        
        await processSaveAs(createdFormat.id, 0);
      }
    },
    [props.datasheet, props.getTabElements, props.defaultFieldComponentSettings, props.defaultTextComponentSettings, props.onSaveComplete, props.tableFieldId, closeFolderPicker, showConfirmMessage]
  );

  const handleSaveSubmit = React.useCallback(
    async (context: number) => {
      try {
        setIsSaving(true);
        const isTDS = props.tableFieldId != null && props.tableFieldId !== 0;
        const tabs: number[] = [];
        props.datasheet.tabs.forEach((tab: LocalDatasheetTabDto, index: number) => {
          const tabId = typeof tab.id === 'string' ? index + 1 : tab.id;
          tabs.push(tabId);
        });

        const notLoaded = await getNotLoadedDatasheetComponents({
          formatId: props.datasheet.id,
          tabs,
          ...(isTDS ? { isTDS: true } : {}),
        });

        const mergedTabs = mergeNotLoadedIntoTabs(props.datasheet.tabs, notLoaded);

        const formatPayload = convertDatasheetTabsToApiPayload(
          mergedTabs,
          formatId,
          formatId,
          props.getTabElements,
          notLoaded
        );

        await updateDatasheetFormat({
          id: formatPayload.id,
          oldFormatId: formatPayload.oldFormatId,
          tabsData: formatPayload.tabsData,
          tds: isTDS,
        });

        await saveFormatDefaults({
          formatId,
          context: context,
          defaults: convertUIFormatsToApiDefaults(
            props.defaultFieldComponentSettings,
            props.defaultTextComponentSettings,
            props.datasheet.gridSettings,
            formatId,
            context
          ),
          tds: isTDS,
        });

        setSaveSuccessMessage(trDts('Datasheet format saved successfully'));
      } catch (error) {
        console.error("Failed to save datasheet format", error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [props.datasheet, formatId, props.getTabElements, props.defaultFieldComponentSettings, props.defaultTextComponentSettings, props.tableFieldId]
  );


  return {
    folderPickerOpened,
    openFolderPicker,
    closeFolderPicker,
    handleSaveAsSubmit,
    handleSaveSubmit,
    showInfoMessage,
    messageProps,
    closeMessage,
    saveSuccessMessage,
    clearSaveSuccessMessage,
    isSaving,
  };
};